import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, Button, Card, CardContent, CardActions, Grid, Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Link } from '@mui/material';
import TokenIcon from '@mui/icons-material/Token';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import {colorBlue, colorText} from "../App/theme";
import { createSubscription, cancelSubscription } from '../API/Stripe';
import { getUserProfile } from "../API/User";
import {parkedDomain, sleep} from "../App/utils";
import { getDomainPublicSettings } from "../API/KB/KBAPI";
import LocalLoader from "../Loaders/LocalLoader";

const formatPrice = (price) => {
    return `${(price / (1000)).toFixed(2)} credits`;
};

export function hasSubscription (userProfile) {
    return userProfile?.subscriptionPlan && userProfile?.subscriptionPlan !== "free";
}



const precision = (model) => {
    if (model?.size === 'Large') return 3
    if (model?.size === 'Medium') return 4
    return 5
}

export const renderServicePrices = (model) => {
    const priceTypes = {
        inputPrice: { label: ' / 1K input tokens', precision: precision(model) },
        outputPrice: { label: ' / 1K output tokens', precision: precision(model) },
        visionPricePerImage: { label: ' / image', precision: precision(model) },
        pricePerRequest: { label: ' / request', precision: 4 },
        pricePerInteraction: { label: ' / interaction', precision: 4 },
        pricePerMinute: { label: ' / minute', precision: 4 },
        pricePerCharacter: { label: ' / character', precision: 5 },
    };

    const formattedPrices = Object.entries(priceTypes).map(([key, { label, precision }]) => {
        if (model[key] !== undefined) {
            return `${formatPrice(model[key])}${label}`;
        }
        return null;
    }).filter(price => price !== null);

    return formattedPrices.join(', ');
};

const getSubscriptionPlans = () => {
    return [
        {
            id: 'free',
            name: 'Free',
            price: 0,
            credits: 0,
            agents: 0,
            knowledgeBase: 0.1,
            fileStorage: 0.5,
            whiteLabel: false,
            customDomain: false,
            website: false,
            automatedTasks: false,
            stripeIntegration: false,
            yourOwnKeys: false,
            description: '* Not for production',
            targetAudience: 'For testing & development',
            currency: 'EUR',
            symbol: '€'
        },
        {
            id: 'starter',
            name: 'Starter',
            price: 19,
            credits: 1500,
            knowledgeBase: 0.2,
            usageBasedCloudAndAI: true,
            customDomain: true,
            website: true,
            description: '1,500 credits',
            targetAudience: 'Build and test your first AI project',
            currency: 'EUR',
            symbol: '€'
        },
        {
            id: 'pro',
            name: 'Pro',
            price: 99,
            credits: 10000,
            knowledgeBase: 2.0,
            usageBasedCloudAndAI: true,
            customDomain: true,
            website: true,
            description: '10,000 credits',
            targetAudience: 'For agencies ready to sell to first clients',
            currency: 'EUR',
            symbol: '€'
        },
        {
            id: 'business',
            name: 'Business',
            price: 499,
            credits: 60000,
            knowledgeBase: 10.0,
            usageBasedCloudAndAI: true,
            customDomain: true,
            website: true,
            description: '60,000 credits',
            targetAudience: 'For agencies scaling their business',
            popular: true,
            currency: 'EUR',
            symbol: '€'
        },
        // Enterprise plan temporarily removed
    ];
};

const PlanCard = ({ plan, currentPlan, onSubscribe, onCancel, isLoading }) => {
    // Special thin rendering for Developer plan
    if (plan.id === 'free') {
        const isCurrentPlan = currentPlan === plan.id;
        return (
            <Card sx={{
                position: 'relative',
                height: 'auto',
                border: isCurrentPlan ? '2px solid #1976d2' : '1px solid #e0e0e0',
                p: 1.5
            }}>
                {isCurrentPlan && (
                    <Box sx={{
                        position: 'absolute',
                        top: 10,
                        right: 20,
                        backgroundColor: colorBlue,
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem'
                    }}>
                        Current
                    </Box>
                )}
                <Box sx={{ textAlign: 'center', pt: 1 }}>
                    <Typography variant="h6" component="h2">
                        {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {plan.targetAudience}
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={true}
                        sx={{ mt: 2 }}
                    >
                        {isCurrentPlan ? 'Current Plan' : 'Free Plan'}
                    </Button>
                </Box>
            </Card>
        );
    }

    // Regular rendering for paid plans
    const isCurrentPlan = currentPlan === plan.id;
    const isFreePlan = plan.id === 'free';
    const isOnFreePlan = currentPlan === 'free';
    const isOnPaidPlan = currentPlan && currentPlan !== 'free';

    const getButtonText = () => {
        if (isCurrentPlan && isFreePlan) return 'Current Plan';
        if (isCurrentPlan && !isFreePlan) return 'Cancel Subscription';
        if (isFreePlan) return 'Free Plan';
        return 'Subscribe';
    };

    const getButtonAction = () => {
        if (isCurrentPlan && !isFreePlan) return onCancel; // Only current paid plan can be canceled
        if (!isCurrentPlan && !isFreePlan && isOnFreePlan) return () => onSubscribe(plan.id); // Subscribe only if on free plan
        return null; // All other cases are disabled
    };

    const getButtonVariant = () => {
        if (isCurrentPlan && !isFreePlan) return 'outlined'; // Cancel button
        return 'contained'; // Subscribe buttons
    };

    const isButtonDisabled = () => {
        if (isLoading) return true;
        if (isCurrentPlan && isFreePlan) return true; // Current free plan - disabled
        if (isCurrentPlan && !isFreePlan) return false; // Current paid plan - active (cancel)
        if (!isCurrentPlan && !isFreePlan && isOnFreePlan) return false; // Other paid plans when on free - active (subscribe)
        return true; // All other cases - disabled (when on paid plan, other plans are disabled)
    };

    return (
        <Card sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: isCurrentPlan ? '2px solid #1976d2' : '1px solid #e0e0e0'
        }}>
            {plan.popular && !isCurrentPlan && (
                <Box sx={{
                    position: 'absolute',
                    top: 10,
                    right: 20,
                    backgroundColor: '#ff9800',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                }}>
                    Most Popular
                </Box>
            )}
            {isCurrentPlan && (
                <Box sx={{
                    position: 'absolute',
                    top: 10,
                    right: 20,
                    backgroundColor: colorBlue,
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                }}>
                    Current
                </Box>
            )}
            <CardContent sx={{ pb: 1 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                    {plan.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontStyle: 'italic', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {plan.targetAudience || ' '}
                </Typography>
                <Typography variant="h4" component="p" color="primary" gutterBottom>
                    {plan.price === 0 ? 'Free' : plan.price === null ? 'Custom' : `€${plan.price}`}
                    {plan.price > 0 && <Typography variant="body2" component="span" color="text.secondary">/mo (excl. VAT)</Typography>}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {plan.description}
                </Typography>
                <Box sx={{ mt: 2 }}>
                    {/* Hide credits for current plan since user may be on a variant with different credits */}
                    {!isCurrentPlan && <Typography variant="body2">
                        <TokenIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'success.main' }} />
                        Credits: {plan.credits === 0 ? '–' : `${plan.credits.toLocaleString()}`}
                    </Typography>}

                    {/* Show all other features dynamically */}
                    {Object.entries(plan).map(([key, value]) => {
                        // Skip already rendered fields and metadata fields
                        const skipFields = ['id', 'name', 'price', 'credits', 'agents', 'knowledgeBase', 'fileStorage',
                                          'description', 'targetAudience', 'currency', 'symbol', 'stripe_price_id', 'stripe_test_price_id',
                                          'popular', 'contactOnly', 'contactEmail', 'maxCredits', 'priceVariants', 'hidden', 'support'];

                        if (skipFields.includes(key)) return null;

                        // Format the feature name
                        const labelMap = { usageBasedCloudAndAI: 'Usage-based Cloud & AI' };
                        const featureName = labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

                        // Show boolean features
                        if (typeof value === 'boolean') {
                            if (value) {
                                // Active feature - show with checkmark
                                return (
                                    <Typography key={key} variant="body2">
                                        <CheckCircle sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'success.main' }} />
                                        {featureName}
                                    </Typography>
                                );
                            } else {
                                // Inactive feature - show with X icon
                                return (
                                    <Typography key={key} variant="body2" sx={{ color: 'text.disabled' }}>
                                        <Cancel sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'text.disabled' }} />
                                        {featureName}
                                    </Typography>
                                );
                            }
                        }

                        // Show string/number features
                        if (value !== undefined && value !== null && value !== '') {
                            return (
                                <Typography key={key} variant="body2">
                                    <CheckCircle sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'success.main' }} />
                                    {featureName}: {value}
                                </Typography>
                            );
                        }

                        return null;
                    })}
                </Box>
            </CardContent>
            <Box sx={{ flexGrow: 1 }} />
            <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                    variant={getButtonVariant()}
                    fullWidth
                    disabled={isButtonDisabled()}
                    onClick={getButtonAction()}
                    color={isCurrentPlan && !isFreePlan ? 'error' : 'primary'}
                >
                    {getButtonText()}
                </Button>
            </CardActions>
        </Card>
    );
};

const style = { p: 3, paddingBottom: 1, paddingTop: 2 , mb: 2, textAlign: 'left'  }
const baseStyle = { marginLeft: '-30px', marginRight: '-30px' };

export const Pricing = () => {
    const [userProfile, setUserProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, plan: null, action: null });
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [creditPricing, setCreditPricing] = useState(null);

    useEffect(() => {
        loadUserProfile();
        loadSubscriptionPlans();
    }, []);

    const loadSubscriptionPlans = async () => {
        // First check if there's a parked domain with custom plans
        if (parkedDomain) {
            try {
                const settings = await getDomainPublicSettings(parkedDomain);

                // Load credit pricing if available
                if (settings?.CREDIT_PRICING) {
                    setCreditPricing(settings.CREDIT_PRICING);
                }

                if (settings && settings.SUBSCRIPTION_PLANS && settings.SUBSCRIPTION_PLANS.length > 0) {
                    // Check URL for variant parameter
                    const urlParams = new URLSearchParams(window.location.search);
                    const urlPlanId = urlParams.get('plan');
                    const urlVariantId = urlParams.get('variant');

                    // Use custom plans as-is without adding default features
                    const customPlans = settings.SUBSCRIPTION_PLANS.map(plan => {
                        let planData = {
                            ...plan,
                            // Use the name from the plan directly, no conversion needed
                            name: plan.name || plan.id
                        };

                        // If URL specifies a variant for this plan, override credits with variant credits
                        if (urlVariantId && plan.id === urlPlanId && plan.priceVariants) {
                            const variant = plan.priceVariants.find(v => v.variantId === urlVariantId);
                            if (variant) {
                                planData.credits = variant.credits || plan.credits;
                                planData.maxCredits = variant.maxCredits || variant.credits || plan.maxCredits;
                            }
                        }

                        return planData;
                    });

                    // Always include free plan at the beginning
                    const freePlan = {
                        id: 'free',
                        name: 'Free',
                        price: 0,
                        credits: 0,
                        agents: 0,
                        knowledgeBase: 0.1,
                        fileStorage: 0.5,
                        whiteLabel: false,
                        customDomain: false,
                        website: false,
                        automatedTasks: false,
                        stripeIntegration: false,
                        yourOwnKeys: false,
                        description: '* Not for production',
                        targetAudience: 'For testing & development',
                        currency: 'EUR',
                        symbol: '€'
                    };

                    setSubscriptionPlans([freePlan, ...customPlans]);
                    return;
                }
            } catch (error) {
                console.error('Error loading custom subscription plans:', error);
            }
        }

        // Fall back to default plans if no custom plans found
        setSubscriptionPlans(getSubscriptionPlans());
    };

    const loadUserProfile = async () => {
        try {
            setIsLoading(true);
            const profile = await getUserProfile();
            setIsLoading(false);
            setUserProfile(profile);
        } catch (error) {
            console.error('Error loading user profile:', error);
        }
    };

    const handleSubscribe = async (planId) => {
        const plan = subscriptionPlans.find(p => p.id === planId);

        // Check if plan is contact-only
        if (plan?.contactOnly) {
            setError(`For ${plan.name} plan, please contact ${plan.contactEmail}`);
            return;
        }

        // Skip confirmation dialog and go directly to Stripe
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const result = await createSubscription(plan.id, userProfile);
            if (result.url) {
                window.location.href = result.url;
            }
        } catch (error) {
            setError(error.message || 'An error occurred');
            setIsLoading(false);
        }
    };

    const handleCancel = async () => {
        setConfirmDialog({
            open: true,
            plan: subscriptionPlans.find(p => p.id === 'free'),
            action: 'cancel'
        });
    };

    const executeAction = async () => {
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const { action } = confirmDialog;

            // Only handle cancel action now (subscribe goes directly to Stripe)
            if (action === 'cancel') {
                if (userProfile.subscriptionId) {
                    await cancelSubscription(userProfile.subscriptionId);
                    setSuccess('Canceling subscription...');

                    // Poll until cancellation is reflected in the profile
                    let attempts = 0;
                    const maxAttempts = 30; // 15 seconds max

                    while (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                        const updatedProfile = await getUserProfile();

                        if (updatedProfile && (updatedProfile.subscriptionPlan === 'free' || !updatedProfile.subscriptionId)) {
                            setUserProfile(updatedProfile);
                            setSuccess('Subscription canceled successfully!');
                            break;
                        }
                        attempts++;
                    }

                    if (attempts >= maxAttempts) {
                        setSuccess('Subscription cancellation initiated. Changes may take a few minutes to reflect.');
                        await loadUserProfile(); // Load whatever we have
                    }
                }
            }
        } catch (error) {
            setError(error.message || 'An error occurred');
        } finally {
            setIsLoading(false);
            setConfirmDialog({ open: false, plan: null, action: null });
        }
    };

    const currentPlan = userProfile?.subscriptionPlan || 'free';
    const isActive = userProfile?.subscriptionStatus === 'active';

    if (!userProfile) return <LocalLoader open={isLoading} blocking={true} />;

    const planId = subscriptionPlans.find(p => p.id === currentPlan)?.id

    return (
        <Container sx={{ mt: 4 }} style={{ color: colorText, textAlign: 'left', marginTop: '-10px' }}>
            <Paper elevation={0} sx={style} style={baseStyle}>
                <Typography variant="h6" gutterBottom>
                    Subscription Plans
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {userProfile && (
                    <Box sx={{ mb: 3, p: 2, borderRadius: 1}}>
                        <Grid container alignItems="center" spacing={2}>
                            {/* Current Plan Info - Left Side */}
                            <Grid item xs={12} md={currentPlan === 'free' ? 6 : 12}>
                                <Typography variant="body1">
                                    <strong>Current Plan:</strong> {subscriptionPlans.find(p => p.id === currentPlan)?.name || 'Free'}
                                </Typography>
                                {planId !== 'free' && <Typography variant="body2" color="text.secondary">
                                    Status: {isActive ? 'Active' : 'Inactive'}
                                </Typography>}
                                {userProfile.currentPeriodEnd && (
                                    <Typography variant="body2" color="text.secondary">
                                        Next billing: {new Date(userProfile.currentPeriodEnd).toLocaleDateString()}
                                    </Typography>
                                )}
                            </Grid>

                            {/* Free Plan Card - Right Side (only when on free plan) */}
                            {currentPlan === 'free' && (
                                <Grid item xs={12} md={6}>
                                    <Card sx={{
                                        border: '1px solid #e0e0e0',
                                        p: 1.5
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="h6">{subscriptionPlans.find(p => p.id === 'free')?.name} Plan</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {subscriptionPlans.find(p => p.id === 'free')?.targetAudience}
                                                </Typography>
                                            </Box>
                                            <Button variant="outlined" size="small" disabled>
                                                Current
                                            </Button>
                                        </Box>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}

                <Grid container spacing={3} justifyContent="center">
                    {subscriptionPlans.filter(p => p.id !== 'free' && (!p.hidden || p.id === currentPlan)).map((plan) => (
                        <Grid item xs={12} sm={6} md={3} key={plan.id}>
                            <PlanCard
                                plan={plan}
                                currentPlan={currentPlan}
                                onSubscribe={handleSubscribe}
                                onCancel={handleCancel}
                                isLoading={isLoading}
                            />
                        </Grid>
                    ))}
                </Grid>

                {/* Info about bonus credits accumulation */}
                {!parkedDomain && <Box sx={{ mt: 3, p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>How bonus credits work:</strong> Monthly bonus credits accumulate up to 2x your plan's allocation.
                        Unused credits roll over each month until you reach the cap. Once at the cap, you'll receive only enough credits to maintain it.
                    </Typography>
                </Box>}

                {hasSubscription(userProfile) && creditPricing?.disabled !== true && (
                    <Box sx={{ mt: 3, textAlign: 'left' }}>
                        <Typography variant="body1">
                            <Link
                                href="/billing"
                                underline="hover"
                                sx={{
                                    color: '#1976d2',
                                    fontWeight: 500,
                                    fontSize: '1.1rem'
                                }}
                            >
                                {creditPricing
                                    ? `Buy ${creditPricing.creditsPer10Units} additional credits for ${creditPricing.symbol}10`
                                    : `Buy 1000 additional credits for €10`}
                            </Link>
                        </Typography>
                    </Box>
                )}
            </Paper>

            <Dialog open={confirmDialog.open} onClose={isLoading ? undefined : () => setConfirmDialog({ open: false, plan: null, action: null })}>
                <DialogTitle>
                    Confirm Cancellation
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period and return to the free plan.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, plan: null, action: null })} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={executeAction} variant="contained" disabled={isLoading}>
                        {isLoading ? 'Processing...' : 'Cancel Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};